import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Settings,
  CreditCard,
  FileUp,
  DollarSign,
  Bell,
  Flag,
  Shield,
  FileText,
  Save,
  RefreshCw,
  Plus,
  Trash2,
  Edit2,
  Check,
  X,
} from "lucide-react";
import adminService from "@/services/adminService";
import { toast } from "sonner";

const SETTING_CATEGORIES = {
  files: { icon: FileUp, label: "Files & Uploads", color: "from-pink-500 to-purple-500" },
  currencies: { icon: DollarSign, label: "Currencies", color: "from-purple-500 to-blue-500" },
  pricing: { icon: CreditCard, label: "Pricing Rules", color: "from-blue-500 to-cyan-500" },
  payments: { icon: CreditCard, label: "Payments", color: "from-cyan-500 to-teal-500" },
  notifications: { icon: Bell, label: "Notifications", color: "from-teal-500 to-green-500" },
  auth: { icon: Shield, label: "Auth & Access", color: "from-green-500 to-yellow-500" },
  features: { icon: Flag, label: "Feature Flags", color: "from-yellow-500 to-orange-500" },
  templates: { icon: FileText, label: "Templates", color: "from-orange-500 to-red-500" },
};

const SETTING_CATEGORY_MAP = {
  allowed_doc_types: "files",
  allowed_audio_types: "files",
  max_doc_mb: "files",
  max_demo_audio_mb: "files",
  max_chapter_audio_mb: "files",
  local_storage_path: "files",
  demo_audio_public: "files",
  enabled_currencies: "currencies",
  default_currency: "currencies",
  encryption_fee_rules: "pricing",
  crowdfunding_encryption_included: "pricing",
  payment_provider_mode: "payments",
  stripe_enabled: "payments",
  paypal_enabled: "payments",
  email_enabled: "notifications",
  outbox_mode: "notifications",
  websocket_enabled: "notifications",
  google_login_enabled: "auth",
  admin_allowlist_emails: "auth",
  role_defaults: "auth",
  public_narrator_profiles: "features",
  public_publisher_profiles: "features",
  enable_campaign_audio_preview: "features",
  templates: "templates",
};

export default function AdminSettings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("files");
  const [editingKey, setEditingKey] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSetting, setNewSetting] = useState({ key: "", value: "" });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await adminService.getSettings();
      setSettings(data);
    } catch (error) {
      console.error("Error loading settings:", error);
      if (error.response?.status === 401) {
        toast.error("Session expired");
        navigate("/login");
      } else if (error.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/dashboard");
      } else {
        toast.error("Failed to load settings");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSetting = async (key, newValue) => {
    setSaving(true);
    try {
      await adminService.updateSetting(key, { value: newValue });
      toast.success(`Setting '${key}' updated`);
      setEditingKey(null);
      await loadSettings();
    } catch (error) {
      toast.error("Failed to save setting");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleSetting = async (key, currentValue) => {
    const setting = settings.find((s) => s.key === key);
    if (!setting) return;

    const newValue = !currentValue;
    try {
      await adminService.updateSetting(key, { value: newValue });
      setSettings((prev) =>
        prev.map((s) =>
          s.key === key ? { ...s, value_json: { value: newValue } } : s
        )
      );
      toast.success(`${key} ${newValue ? "enabled" : "disabled"}`);
    } catch (error) {
      toast.error("Failed to update setting");
    }
  };

  const handleAddCurrency = async () => {
    const setting = settings.find((s) => s.key === "enabled_currencies");
    if (!setting) return;

    const currencies = setting.value_json?.value || [];
    const newCurrency = prompt("Enter currency code (e.g., JPY):");
    if (!newCurrency) return;

    const code = newCurrency.toUpperCase().trim();
    if (currencies.includes(code)) {
      toast.error("Currency already exists");
      return;
    }

    try {
      await adminService.updateSetting("enabled_currencies", {
        value: [...currencies, code],
      });
      toast.success(`Currency ${code} added`);
      await loadSettings();
    } catch (error) {
      toast.error("Failed to add currency");
    }
  };

  const handleRemoveCurrency = async (code) => {
    const setting = settings.find((s) => s.key === "enabled_currencies");
    if (!setting) return;

    const currencies = setting.value_json?.value || [];
    try {
      await adminService.updateSetting("enabled_currencies", {
        value: currencies.filter((c) => c !== code),
      });
      toast.success(`Currency ${code} removed`);
      await loadSettings();
    } catch (error) {
      toast.error("Failed to remove currency");
    }
  };

  const handleResetSettings = async () => {
    if (!window.confirm("Reset all settings to defaults? This cannot be undone.")) return;

    try {
      await adminService.resetSettings();
      toast.success("Settings reset to defaults");
      await loadSettings();
    } catch (error) {
      toast.error("Failed to reset settings");
    }
  };

  const getSettingsByCategory = (category) => {
    return settings.filter((s) => SETTING_CATEGORY_MAP[s.key] === category);
  };

  const getValue = (setting) => {
    return setting?.value_json?.value ?? setting?.value_json;
  };

  const isReadonly = (setting) => {
    return setting?.value_json?.readonly === true;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Platform Settings</h1>
            <p className="text-slate-400 mt-1">Configure system-wide settings</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleResetSettings}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reset to Defaults
            </Button>
          </div>
        </div>

        {/* Settings Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-800/50 border border-slate-700 flex-wrap h-auto p-1">
            {Object.entries(SETTING_CATEGORIES).map(([key, { icon: Icon, label }]) => (
              <TabsTrigger
                key={key}
                value={key}
                className="text-white data-[state=active]:bg-gradient-to-r data-[state=active]:from-pink-500 data-[state=active]:via-purple-500 data-[state=active]:to-blue-500 data-[state=active]:text-white"
              >
                <Icon className="w-4 h-4 mr-2" />
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* FILES TAB */}
          <TabsContent value="files" className="space-y-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileUp className="w-5 h-5" />
                  File Upload Configuration
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Size Limits */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {["max_doc_mb", "max_demo_audio_mb", "max_chapter_audio_mb"].map((key) => {
                    const setting = settings.find((s) => s.key === key);
                    const value = getValue(setting);
                    const label = key.replace(/_/g, " ").replace("mb", "MB").replace("max", "Max");
                    
                    return (
                      <div key={key} className="space-y-2">
                        <Label className="text-slate-300 capitalize">{label}</Label>
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            value={editingKey === key ? editValue : value}
                            onChange={(e) => {
                              if (editingKey === key) {
                                setEditValue(e.target.value);
                              } else {
                                setEditingKey(key);
                                setEditValue(e.target.value);
                              }
                            }}
                            onFocus={() => {
                              setEditingKey(key);
                              setEditValue(value?.toString() || "");
                            }}
                            className="bg-slate-900 border-slate-600 text-white"
                          />
                          {editingKey === key && (
                            <Button
                              size="sm"
                              onClick={() => handleSaveSetting(key, parseInt(editValue))}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Demo Audio Public */}
                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">Demo Audio Public by Default</p>
                    <p className="text-sm text-slate-400">Narrator demo audio is visible to all users</p>
                  </div>
                  <Switch
                    checked={getValue(settings.find((s) => s.key === "demo_audio_public")) ?? true}
                    onCheckedChange={() =>
                      handleToggleSetting(
                        "demo_audio_public",
                        getValue(settings.find((s) => s.key === "demo_audio_public"))
                      )
                    }
                  />
                </div>

                {/* Storage Path (readonly) */}
                <div className="p-4 bg-slate-900/50 rounded-lg">
                  <Label className="text-slate-300">Local Storage Path</Label>
                  <div className="flex items-center gap-2 mt-2">
                    <Input
                      value={getValue(settings.find((s) => s.key === "local_storage_path")) || "/app/backend/uploads"}
                      disabled
                      className="bg-slate-800 border-slate-600 text-slate-400"
                    />
                    <Badge className="bg-slate-700 text-slate-300">Read-only</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* CURRENCIES TAB */}
          <TabsContent value="currencies" className="space-y-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Currency Management
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enabled Currencies */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-slate-300">Enabled Currencies</Label>
                    <Button size="sm" onClick={handleAddCurrency}>
                      <Plus className="w-4 h-4 mr-1" />
                      Add Currency
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(getValue(settings.find((s) => s.key === "enabled_currencies")) || []).map((code) => (
                      <Badge
                        key={code}
                        className="bg-gradient-to-r from-pink-500/20 to-purple-500/20 text-white border border-purple-500/50 px-3 py-1"
                      >
                        {code}
                        <button
                          onClick={() => handleRemoveCurrency(code)}
                          className="ml-2 text-slate-400 hover:text-red-400"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Default Currency */}
                <div className="space-y-2">
                  <Label className="text-slate-300">Default Currency</Label>
                  <select
                    value={getValue(settings.find((s) => s.key === "default_currency")) || "USD"}
                    onChange={(e) => handleSaveSetting("default_currency", e.target.value)}
                    className="w-full p-2 bg-slate-900 border border-slate-600 rounded-md text-white"
                  >
                    {(getValue(settings.find((s) => s.key === "enabled_currencies")) || ["USD"]).map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PAYMENTS TAB */}
          <TabsContent value="payments" className="space-y-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Payment Configuration
                </CardTitle>
                <CardDescription className="text-slate-400">
                  Payment provider is currently mocked. Real providers can be enabled when API keys are configured.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Provider Mode */}
                <div className="space-y-2">
                  <Label className="text-slate-300">Payment Provider Mode</Label>
                  <select
                    value={getValue(settings.find((s) => s.key === "payment_provider_mode")) || "mock"}
                    onChange={(e) => handleSaveSetting("payment_provider_mode", e.target.value)}
                    className="w-full p-2 bg-slate-900 border border-slate-600 rounded-md text-white"
                  >
                    <option value="mock">Mock (Testing)</option>
                    <option value="stripe">Stripe</option>
                    <option value="paypal">PayPal</option>
                    <option value="auto">Auto (Use available)</option>
                  </select>
                </div>

                {/* Stripe / PayPal Toggles */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg">
                    <div>
                      <p className="text-white font-medium">Stripe</p>
                      <p className="text-sm text-slate-400">Enable Stripe payments</p>
                    </div>
                    <Switch
                      checked={getValue(settings.find((s) => s.key === "stripe_enabled")) ?? false}
                      onCheckedChange={() =>
                        handleToggleSetting(
                          "stripe_enabled",
                          getValue(settings.find((s) => s.key === "stripe_enabled"))
                        )
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg">
                    <div>
                      <p className="text-white font-medium">PayPal</p>
                      <p className="text-sm text-slate-400">Enable PayPal payments</p>
                    </div>
                    <Switch
                      checked={getValue(settings.find((s) => s.key === "paypal_enabled")) ?? false}
                      onCheckedChange={() =>
                        handleToggleSetting(
                          "paypal_enabled",
                          getValue(settings.find((s) => s.key === "paypal_enabled"))
                        )
                      }
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* AUTH TAB */}
          <TabsContent value="auth" className="space-y-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Authentication & Access
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg">
                  <div>
                    <p className="text-white font-medium">Google Login</p>
                    <p className="text-sm text-slate-400">Allow users to sign in with Google</p>
                  </div>
                  <Switch
                    checked={getValue(settings.find((s) => s.key === "google_login_enabled")) ?? true}
                    onCheckedChange={() =>
                      handleToggleSetting(
                        "google_login_enabled",
                        getValue(settings.find((s) => s.key === "google_login_enabled"))
                      )
                    }
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* FEATURES TAB */}
          <TabsContent value="features" className="space-y-4">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Flag className="w-5 h-5" />
                  Feature Flags
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { key: "public_narrator_profiles", label: "Public Narrator Profiles", desc: "Allow public access to narrator profiles" },
                  { key: "public_publisher_profiles", label: "Public Publisher Profiles", desc: "Allow public access to publisher profiles" },
                  { key: "enable_campaign_audio_preview", label: "Campaign Audio Preview", desc: "Show audio preview on funding campaigns" },
                ].map(({ key, label, desc }) => (
                  <div key={key} className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg">
                    <div>
                      <p className="text-white font-medium">{label}</p>
                      <p className="text-sm text-slate-400">{desc}</p>
                    </div>
                    <Switch
                      checked={getValue(settings.find((s) => s.key === key)) ?? true}
                      onCheckedChange={() =>
                        handleToggleSetting(key, getValue(settings.find((s) => s.key === key)))
                      }
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Other tabs can be added similarly */}
          <TabsContent value="pricing">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6 text-center text-slate-400">
                Pricing rules configuration coming soon
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="notifications">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6 text-center text-slate-400">
                Notification settings configuration coming soon
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="templates">
            <Card className="bg-slate-800/50 border-slate-700">
              <CardContent className="p-6 text-center text-slate-400">
                Email template editor coming soon
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
  );
}
